import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, MapPin, ArrowRight } from "lucide-react";
import { trackHome } from "@/lib/requestContext";


/**
 * Big hero search bar with a working segmented selector.
 * The active tab drives both the placeholder copy and the routed query
 * (?type=local|digital), then navigates to the provider directory.
 */
const MODES = [
  { key: "all", label: "All", placeholder: "What do you need done?", testid: "search-mode-all" },
  { key: "local", label: "Book Local", placeholder: "Search local pros — cleaning, handyman, lawn…", testid: "search-mode-local" },
  { key: "digital", label: "Hire Digital", placeholder: "Search digital talent — design, dev, marketing…", testid: "search-mode-digital" },
];

export default function HeroSearchBar() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("all");
  const [q, setQ] = useState("");
  const [where, setWhere] = useState("");

  const active = MODES.find((m) => m.key === mode) || MODES[0];

  const submit = (e) => {
    trackHome("search_start");
    e?.preventDefault?.();
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (where) params.set("where", where);
    if (mode !== "all") params.set("type", mode);
    navigate(`/providers?${params.toString()}`);
  };

  return (
    <div className="w-full max-w-4xl">
      <div className="inline-flex items-center gap-1 rounded-full border border-white/20 bg-white/10 backdrop-blur-md p-1 mb-4" role="tablist" aria-label="Search type">
        {MODES.map((m) => (
          <button
            key={m.key}
            type="button"
            role="tab"
            aria-selected={mode === m.key}
            onClick={() => setMode(m.key)}
            data-testid={m.testid}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
              mode === m.key ? "bg-white text-[#071b22] shadow-sm" : "text-white/80 hover:text-white"
            }`}
          >
            {m.label}
          </button>
        ))}
      </div>

      <form
        onSubmit={submit}
        className="flex flex-col md:flex-row md:items-center gap-2 md:gap-0 rounded-2xl md:rounded-full border border-white/25 bg-white/95 backdrop-blur-xl p-2 md:p-1.5 md:pl-2 shadow-[0_20px_60px_rgba(7,27,34,0.35)]"
        data-testid="hero-search-form"
      >
        <label className="flex-1 flex items-center gap-3 h-12 px-3 md:px-4 rounded-xl md:rounded-full">
          <Search className="h-5 w-5 shrink-0 text-[#165a71]" />
          <input
            type="text"
            placeholder={active.placeholder}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full h-full bg-transparent outline-none text-base text-[#071b22] placeholder:text-[#252A34]/50"
            data-testid="hero-search-query"
          />
        </label>

        <div className="hidden md:block h-7 w-px bg-slate-200 mx-1 shrink-0" />

        <label className="flex-1 flex items-center gap-3 h-12 px-3 md:px-4 rounded-xl md:rounded-full">
          <MapPin className="h-5 w-5 shrink-0 text-[#165a71]" />
          <input
            type="text"
            placeholder="City, state, or ZIP"
            value={where}
            onChange={(e) => setWhere(e.target.value)}
            className="w-full h-full bg-transparent outline-none text-base text-[#071b22] placeholder:text-[#252A34]/50"
            data-testid="hero-search-location"
          />
        </label>

        <button
          type="submit"
          data-testid="hero-search-submit"
          className="shrink-0 inline-flex items-center justify-center gap-2 h-12 rounded-xl md:rounded-full bg-[#165a71] px-6 text-white font-medium hover:bg-[#11485a] transition-colors"
        >
          Search <ArrowRight className="h-4 w-4" />
        </button>
      </form>
    </div>
  );
}
